'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import Image from 'next/image'
import styles from './Media.module.scss'
import { ComponentProps, RepeaterField } from '@/types/database'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeBasicHtml } from '@/lib/utils/html-sanitizer'
import { CustomPlayButton } from '@/components/CustomPlayButton'


interface MediaItem extends RepeaterField {
  mediaUrl?: string
  title?: string
  caption?: string
  aspectRatio?: string
  isMainItem?: boolean
}

interface MediaProps extends ComponentProps {
  mediaType?: 'image' | 'video'
  imageUrl?: string
  vimeoId?: string
  videoStyle?: 'embedded' | 'background'
  aspectRatio?: string
  columnAspectRatio?: string
  // Legacy support
  mediaUrl?: string
  title?: string
  caption?: string
  columns?: number
  additionalMedia?: MediaItem[]
  priority?: boolean
  noTopPadding?: boolean
  noBottomPadding?: boolean
}

// Custom hook for individual media item animation
const useMediaItemAnimation = (index: number) => {
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad && !hasAnimated

  useEffect(() => {
    const element = ref.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) {
        setShouldAnimate(true)
        setHasAnimated(true)
      }
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {
          setShouldAnimate(true)
          setHasAnimated(true)
        }
      },
      { threshold: 0.2 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, index, isAdminMode])

  return { ref, shouldAnimate }
}

export const Media: React.FC<MediaProps> = ({
  mediaType = 'image',
  imageUrl,
  vimeoId,
  videoStyle = 'embedded',
  aspectRatio = '16/9',
  columnAspectRatio = '16/9',
  // Legacy support
  mediaUrl = '',
  title = 'Media Title',
  caption = 'Media caption',
  columns = 1,
  additionalMedia = [],
  priority = false,
  noTopPadding = false,
  noBottomPadding = false
}) => {
  const columnsNum = Number(columns) || 1
  const additionalMediaArray = Array.isArray(additionalMedia) ? additionalMedia : []
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  

  
  // Determine the actual media URL for the main item
  let actualMediaUrl: string | null = null
  if (mediaType === 'video' && vimeoId) {
    actualMediaUrl = vimeoId.trim()
  } else if (mediaType === 'image' && imageUrl) {
    actualMediaUrl = imageUrl
  } else if (mediaUrl) {
    actualMediaUrl = mediaUrl
  }
  
  const allMedia = [
    { mediaUrl: actualMediaUrl || '', title, caption, aspectRatio: columnAspectRatio, isMainItem: true },
    ...additionalMediaArray.slice(0, columnsNum - 1).map(item => ({ ...item, isMainItem: false }))
  ].filter(item => item.mediaUrl) // Filter out items without media

  const isVideo = (url: string) => {
    return url.match(/\.(mp4|webm|ogg|mov)$/i)
  }

  // Parse Vimeo ID and hash parameter for unlisted/private videos (same as Hero component)
  const parseVimeoId = (id: string) => {
    if (!id) return { videoId: '', hashParam: '' }
    
    const trimmedId = id.trim()
    const [videoId, hashParam] = trimmedId.split('?h=')
    
    return {
      videoId: videoId || '',
      hashParam: hashParam || ''
    }
  }

  const isVimeoId = (value: string) => {
    if (!value) return false
    
    // Check if it's just numbers (Vimeo ID) or contains vimeo.com or has hash format
    const parsed = parseVimeoId(value)
    const hasValidVideoId = /^\d+$/.test(parsed.videoId)
    const hasValidHash = !parsed.hashParam || /^[a-zA-Z0-9]+$/.test(parsed.hashParam)
    
    return (hasValidVideoId && hasValidHash) || value.includes('vimeo.com')
  }

  const getVimeoId = (value: string) => {
    if (!value) return null
    
    const parsed = parseVimeoId(value)
    if (/^\d+$/.test(parsed.videoId)) {
      return parsed.videoId
    }
    // Extract ID from Vimeo URL
    const match = value.match(/vimeo\.com\/(\d+)/)
    return match ? match[1] : null
  }

  return (
    <section className={styles.media}>
      <div className={`${styles.media__container} ${
        noTopPadding && noBottomPadding ? styles['media__container--no-vertical'] :
        noTopPadding ? styles['media__container--no-top'] :
        noBottomPadding ? styles['media__container--no-bottom'] : ''
      }`}>
        <div className={`${styles.media__grid} ${styles[`media__grid--col${columnsNum}`]}`}>
          {allMedia.map((item, index) => {
            const MediaItem = () => {
              const { ref, shouldAnimate } = useMediaItemAnimation(index)

              return (
                <motion.div 
                  ref={ref}
                  key={index} 
                  className={styles.media__item}
                  initial={{ opacity: isAdminMode ? 1 : 0 }}
                  animate={{ opacity: shouldAnimate ? 1 : 0 }}
                  transition={{
                    duration: 0.8,
                    delay: index * 0.2, // Staggered delay based on index
                    ease: [0.25, 0.46, 0.45, 0.94]
                  }}
                >
                  {item.mediaUrl && (
                    <motion.div 
                      className={styles.media__mediaWrapper}
                      initial={{ opacity: isAdminMode ? 1 : 0 }}
                      animate={{
                        opacity: shouldAnimate ? 1 : 0,
                        y: shouldAnimate ? 0 : 0
                      }}
                      transition={{
                        duration: 0.8,
                        delay: index * 0.2, // Same timing as parent
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                    >
                      {isVimeoId(item.mediaUrl) && getVimeoId(item.mediaUrl) ? (
                        <div 
                          className={styles.media__vimeoWrapper}
                          style={{ aspectRatio: columnsNum > 1 ? (item.aspectRatio || columnAspectRatio) : aspectRatio }}
                        >
                          <iframe 
                            src={`https://player.vimeo.com/video/${getVimeoId(item.mediaUrl)}?${
                              (() => {
                                const parsed = parseVimeoId(item.mediaUrl)
                                const hashParam = parsed.hashParam ? `h=${parsed.hashParam}&` : ''
                                return hashParam + (item.isMainItem && mediaType === 'video' && videoStyle === 'background'
                                  ? 'background=1&autoplay=1&loop=1&byline=0&title=0&portrait=0&muted=1&dnt=1'
                                  : 'badge=0&autopause=0&player_id=0&app_id=58479&byline=0&title=0&portrait=0&color=ffffff&controls=0&muted=1&dnt=1')
                              })()
                            }`}
                            className={styles.media__vimeo}
                            frameBorder="0"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={item.title || 'Vimeo video'}
                          />
                          {/* Only show custom play button for embedded videos, not background videos */}
                          {!(item.isMainItem && mediaType === 'video' && videoStyle === 'background') && (
                            <CustomPlayButton 
                              vimeoId={getVimeoId(item.mediaUrl)!}
                              className={styles.media__customPlayButton}
                            />
                          )}
                        </div>
                      ) : isVideo(item.mediaUrl) ? (
                        <div 
                          className={styles.media__videoWrapper}
                          style={columnsNum > 1 ? { aspectRatio: item.aspectRatio || columnAspectRatio } : {}}
                        >
                          <video 
                            src={item.mediaUrl} 
                            className={styles.media__video}
                            controls
                            playsInline
                            style={columnsNum > 1 ? { width: '100%', height: '100%', objectFit: 'cover' } : {}}
                          />
                        </div>
                      ) : (
                        <div 
                          className={styles.media__imageWrapper}
                          style={columnsNum > 1 ? { aspectRatio: item.aspectRatio || columnAspectRatio } : {}}
                        >
                          <Image 
                            src={item.mediaUrl} 
                            alt={item.title || 'Media'}
                            className={styles.media__image}
                            width={2400}
                            height={1800}
                            quality={95}
                            priority={item.isMainItem ? priority : false}
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, (max-width: 1440px) 1400px, (max-width: 2560px) 2400px, 2800px"
                            style={columnsNum > 1 ? { width: '100%', height: '100%', objectFit: 'cover' } : { width: '100%', height: 'auto' }}
                          />
                        </div>
                      )}
                    </motion.div>
                  )}
                  
                  {item.title && (
                    <motion.h3 
                      className={styles.media__title}
                      initial={{ opacity: isAdminMode ? 1 : 0 }}
                      animate={{
                        opacity: shouldAnimate ? 1 : 0,
                        y: shouldAnimate ? 0 : 0
                      }}
                      transition={{
                        duration: 0.8,
                        delay: index * 0.2 + 0.3, // Title appears after media
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(item.title) }}
                    />
                  )}
                  
                  {item.caption && (
                    <motion.p 
                      className={`${styles.media__caption} ${!item.title ? styles['media__caption--no-title'] : ''}`}
                      initial={{ opacity: isAdminMode ? 1 : 0 }}
                      animate={{
                        opacity: shouldAnimate ? 1 : 0,
                        y: shouldAnimate ? 0 : 0
                      }}
                      transition={{
                        duration: 0.8,
                        delay: index * 0.2 + (item.title ? 0.5 : 0.3), // Caption appears after title, or after media if no title
                        ease: [0.25, 0.46, 0.45, 0.94]
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(item.caption) }}
                    />
                  )}
                </motion.div>
              )
            }
            return <MediaItem key={index} />
          })}
        </div>
      </div>
    </section>
  )
} 